import { MessageCircle } from "lucide-react";
import React, { useEffect, useState } from "react";

const Requirement = () => {
  const [data, setData] = useState([]); // To store fetched data
  const [filters, setFilters] = useState({
    Date: "",
    installationLocation: "",
    buildingType: "",
    energyConsumption: "",
    mobile: "",
  });

  const [filteredData, setFilteredData] = useState([]); // Filtered data to display
  const [isPopupOpen, setIsPopupOpen] = useState(false); // To control popup visibility
  const [popupData, setPopupData] = useState(null); // To store the data for the selected item
  const [results, setResults] = useState({}); // To store calculated results

  const electricityTariff = 8; // Example rate per kWh
  const averageDailySunlightHours = 5; // Example sunlight hours

  // Fetch data from API
  useEffect(() => {
    fetch("https://api.solarstation.in/ask/getAllSolarData")
      .then((response) => response.json())
      .then((result) => {
        const sortedData = result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setData(sortedData);
        setFilteredData(sortedData);
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, []);
  

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    const filtered = data.filter((item) =>
      Object.keys(newFilters).every((key) => {
        if (key === "Date" && newFilters[key]) {
          const filterDate = new Date(newFilters[key]).toISOString().split("T")[0];
          const itemDate = new Date(item.createdAt).toISOString().split("T")[0];
          return itemDate === filterDate;
        }
        return item[key]?.toString().toLowerCase().includes(newFilters[key]?.toLowerCase());
      })
    );
    setFilteredData(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error("Invalid date:", dateString);
      return "Invalid Date";
    }
    const options = {
      year: "2-digit",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-GB", options).replace(",", "").replace(/\//g, "-");
  };
  

  const handleActionClick = (item) => {
    setPopupData(item);
    calculateResults(item);
    setIsPopupOpen(true);
  };

  const calculateResults = (data) => {
    const monthlyUnitConsumption = parseFloat(data.monthlyUnitConsumption);
    const monthlyAverageBill = monthlyUnitConsumption * electricityTariff;
    const dailyUnitConsumption = monthlyUnitConsumption / 30;
    const solarPanelCapacity = dailyUnitConsumption / 4.5;
    const numberOfSolarPanels = Math.ceil(solarPanelCapacity * 2);
    const areaRequireForRooftop = solarPanelCapacity * 95;

    const dailySolarEnergyProduction = solarPanelCapacity * averageDailySunlightHours;
    const dailyElectricityBillSavings = dailySolarEnergyProduction * (electricityTariff - 2);
    const monthlyElectricityBillSavings = dailyElectricityBillSavings * 30;

    const costOfSolarSystem = solarPanelCapacity * 50000;

    const monthlyBillWithSolar = monthlyAverageBill - monthlyElectricityBillSavings;
    const timeToPayOffSolarSystem = costOfSolarSystem / monthlyElectricityBillSavings;
    const annualSavingsFromSolar = monthlyElectricityBillSavings * 12;

    setResults({
      solarPanelCapacity,
      areaRequireForRooftop,
      monthlyAverageBill,
      monthlyBillWithSolar,
      costOfSolarSystem,
      timeToPayOffSolarSystem,
      annualSavingsFromSolar,
      numberOfSolarPanels,
      dailySolarEnergyProduction,
      monthlyElectricityBillSavings,
    });
  };
  const sendToWhatsApp = () => {
    const mobileNumber = popupData.mobile; // User's mobile number
    const message = `Here are the solar calculation results:
    
  - Solar Panel Capacity: ${results.solarPanelCapacity.toFixed(2)} kW
  - Area Required: ${results.areaRequireForRooftop.toFixed(2)} sq.ft
  - Monthly Average Bill: ₹${results.monthlyAverageBill.toFixed(2)}
  - Monthly Bill With Solar: ₹${results.monthlyBillWithSolar.toFixed(2)}
  - Cost of Solar System: ₹${results.costOfSolarSystem.toFixed(2)}
  - Time to Pay Off: ${results.timeToPayOffSolarSystem.toFixed(2)} months
  - Annual Savings: ₹${results.annualSavingsFromSolar.toFixed(2)}
  - Number of Solar Panels: ${results.numberOfSolarPanels}
  
  Contact us for more details!`;
  
    // WhatsApp link
    const whatsappLink = `https://wa.me/${mobileNumber}?text=${encodeURIComponent(message)}`;
  
    // Open the link in a new tab
    window.open(whatsappLink, "_blank");
  };
  
  return (
    <div className="p-4 w-full mx-auto">
    
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Requirement Data</h1>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <input
          type="date"
          name="Date"
          value={filters.Date}
          onChange={handleFilterChange}
          className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <input
          type="text"
          name="installationLocation"
          value={filters.installationLocation}
          onChange={handleFilterChange}
          placeholder="Installation Location"
          className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <input
          type="text"
          name="buildingType"
          value={filters.buildingType}
          onChange={handleFilterChange}
          placeholder="Building Type"
          className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <input
          type="text"
          name="energyConsumption"
          value={filters.energyConsumption}
          onChange={handleFilterChange}
          placeholder="Energy Consumption"
          className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <input
          type="text"
          name="mobile"
          value={filters.mobile}
          onChange={handleFilterChange}
          placeholder="Mobile"
          className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
            <th className="text-left px-2 py-2 border-b">Date</th>
              <th className="text-left px-2 py-2 border-b">Installation Location</th>
              <th className="text-left px-2 py-1 border-b">Building Type</th>
              <th className="text-left px-2 py-2 border-b">Energy Consumption</th>
              <th className="text-left px-4 py-2 border-b">Name</th>
              <th className="text-left px-4 py-2 border-b">Mobile</th>
              <th className="text-left px-4 py-2 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <tr
                  key={index}
                  className={`hover:bg-orange-100 ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-4 py-2 border-b w-1/6">{formatDate(item.createdAt)}</td>
                  <td className="px-4 py-2 border-b">{item.installationLocation}</td>
                  <td className="px-4 py-2 border-b">{item.buildingType}</td>
                  <td className="px-4 py-2 border-b">{item.energyConsumption}</td>
                  <td className="px-4 py-2 border-b">{item.name}</td>
                  <td className="px-4 py-2 border-b">{item.mobile}</td>
                  <td className="px-4 py-2 border-b">
                    <button
                      className="text-orange-500 hover:underline cursor-pointer"
                      onClick={() => handleActionClick(item)}
                    >
                      <MessageCircle />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center text-gray-500 py-4">
                  No data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Popup */}
      {isPopupOpen && popupData && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
      <h2 className="text-lg font-bold mb-4">Calculation Results</h2>
      <div className="mb-4">
        <p><strong>Solar Panel Capacity:</strong> {results.solarPanelCapacity.toFixed(2)} kW</p>
        <p><strong>Area Required:</strong> {results.areaRequireForRooftop.toFixed(2)} sq.ft</p>
        <p><strong>Monthly Average Bill:</strong> ₹{results.monthlyAverageBill.toFixed(2)}</p>
        <p><strong>Monthly Bill With Solar:</strong> ₹{results.monthlyBillWithSolar.toFixed(2)}</p>
        <p><strong>Cost of Solar System:</strong> ₹{results.costOfSolarSystem.toFixed(2)}</p>
        <p><strong>Time to Pay Off:</strong> {results.timeToPayOffSolarSystem.toFixed(2)} months</p>
        <p><strong>Annual Savings:</strong> ₹{results.annualSavingsFromSolar.toFixed(2)}</p>
        <p><strong>Number of Solar Panels:</strong> {results.numberOfSolarPanels}</p>
      </div>
      <div className="flex justify-between mt-4">
        <button
          className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          onClick={() => setIsPopupOpen(false)}
        >
          Close
        </button>
        <button
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          onClick={() => sendToWhatsApp()}
        >
          Send to WhatsApp
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default Requirement;
